import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import authService from '../services/authService';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    const initAuth = async () => {
      try {
        await authService.initialize();
        const user = authService.getCurrentUser();
        if (user) {
          setCurrentUser(user);
          setIsAuthenticated(true);
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const signIn = useCallback(async () => {
    try {
      setIsLoading(true);
      const user = await authService.signIn();
      setCurrentUser(user);
      setIsAuthenticated(true);
      return user;
    } catch (error) {
      if (error.status === 403) {
        setAccessDenied(true);
        return;
      }
      console.error('Sign in failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearAccessDenied = useCallback(() => {
    setAccessDenied(false);
  }, []);

  const signOut = useCallback(async () => {
    await authService.signOut();
    setCurrentUser(null);
    setIsAuthenticated(false);
  }, []);

  // Listen for auth expiration events from the API layer
  useEffect(() => {
    const handleAuthExpired = () => {
      console.warn('Session expired — signing out');
      signOut();
    };
    window.addEventListener('auth:session-expired', handleAuthExpired);
    return () => window.removeEventListener('auth:session-expired', handleAuthExpired);
  }, [signOut]);

  const getCurrentUserPhoto = useCallback(async () => {
    return await authService.getCurrentUserPhoto();
  }, []);

  const isAdmin = useCallback(() => {
    return currentUser?.roles?.includes('Admin') || false;
  }, [currentUser]);

  const value = {
    isAuthenticated,
    isLoading,
    currentUser,
    accessDenied,
    clearAccessDenied,
    signIn,
    signOut,
    getCurrentUserPhoto,
    isAdmin,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
